import { createContext, useContext, ReactNode, useState, useEffect, useMemo } from 'react';
import { useQuery, ApolloClient } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_NAVIGATION_NODE_LISTING_QUERY,
    getNavigationNodeListingQueryVariables,
} from '../queries/getNavigationNodeListingQuery';
import { NavigationNode } from '../types/NavigationNode';
import { transformNodeListingResponse } from '../tools/transformNavigationNodeListingResponse';
import { GetNavigationNodeListingQuery } from '../schemas/generated-types/graphql';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

type NavigationNodeById = Record<string, NavigationNode>;
type NavigationNodeByPath = Record<string, NavigationNode>;

interface NavigationNodeContextValue {
    navigationNodes: NavigationNode[];
    navigationNodeById: NavigationNodeById;
    navigationNodeByPath: NavigationNodeByPath;
    globalMenuNodes: NavigationNode[];
    loading: boolean;
    error: any;
    getNavigationNodeByPath: (path: string) => NavigationNode | undefined;
    initialize: () => void;
    initialized: boolean;
}

const NavigationNodeContext = createContext<NavigationNodeContextValue | undefined>(undefined);

interface NavigationNodesProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const NavigationNodesProvider = ({ children, apolloClient }: NavigationNodesProviderProps) => {
    const { locale } = useLocale();

    const [initialized, setInitialized] = useState(false);

    const query = GET_NAVIGATION_NODE_LISTING_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery<GetNavigationNodeListingQuery>(query, {
        variables: getNavigationNodeListingQueryVariables(locale),
        client: apolloClient,
        errorPolicy: 'all',
        skip: !initialized,
        fetchPolicy: 'network-only',
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized, queryError: queryError });

    const navigationNodes = useMemo(() => {
        const data = queryData ?? fallbackData;
        if (!data) {
            return [];
        }
        return transformNodeListingResponse(data);
    }, [queryData, fallbackData]);

    const navigationNodeById = useMemo(() => {
        return navigationNodes.reduce((acc, n) => {
            acc[n.id] = n;
            return acc;
        }, {} as NavigationNodeById);
    }, [navigationNodes]);

    const navigationNodeByPath = useMemo(() => {
        return navigationNodes.reduce(
            (acc, n) => {
                acc[n.path] = n;
                return acc;
            },
            {} as Record<string, NavigationNode>
        );
    }, [navigationNodes]);

    const globalMenuNodes = useMemo(
        () => navigationNodes.filter((n) => n.menuProperties.isGlobalMenuItem),
        [navigationNodes]
    );

    const initialize = () => {
        if (!initialized) {
            setInitialized(true);
        }
    };

    const getNavigationNodeByPath = (path: string): NavigationNode | undefined => {
        return (
            navigationNodeByPath[path] ||
            navigationNodeByPath['/' + path] ||
            (console.error(`NavigationNode not found for path: ${path}`, navigationNodeByPath),
            navigationNodes[0]) ||
            undefined
        );
    };

    const loading =
        initialized && (loadingQuery || loadingFallback || navigationNodes.length === 0);

    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <NavigationNodeContext.Provider
            value={{
                navigationNodes,
                navigationNodeById,
                navigationNodeByPath,
                globalMenuNodes,
                loading,
                error,
                getNavigationNodeByPath,
                initialize,
                initialized,
            }}
        >
            {children}
        </NavigationNodeContext.Provider>
    );
};

// Unsafe hook: does not trigger initialization.
function useNavigationNodesUnsafe() {
    const context = useContext(NavigationNodeContext);
    if (!context) {
        throw new Error('useNavigationNodesUnsafe must be used within a NavigationNodesProvider');
    }
    return context;
}

// Safe hook: triggers lazy initialization when a consumer uses it.
function useNavigationNodes() {
    const context = useNavigationNodesUnsafe();
    // Trigger lazy initialization when a consumer uses this hook.
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export { NavigationNodesProvider, useNavigationNodes, useNavigationNodesUnsafe };
export type { NavigationNodeContextValue, NavigationNodeById, NavigationNodeByPath };
